import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { TradeBriefingStructureScope } from '../../types';
import {
  buildTradeBriefingTimberDisplayRows,
  formatTradeBriefingBoardFeet,
  sumTradeBriefingBoardFeet,
} from '../../utils/tradeBriefingScope';
import { downloadTradeBriefingTimberPdf } from '../../utils/tradeBriefingTimberPdf';
import {
  filterTradeBriefingTimberRows,
  nextTradeBriefingTimberSort,
  sortTradeBriefingTimberRows,
  uniqueTradeBriefingStructures,
  type TradeBriefingTimberSortDir,
  type TradeBriefingTimberSortKey,
} from '../../utils/tradeBriefingTimberTable';

type TradeBriefingTimberListProps = {
  structures: TradeBriefingStructureScope[];
  projectTitle: string;
  companyName?: string;
  tradeLabel?: string;
};

function structureLabel(name: string, quantity?: number): string {
  if (quantity && quantity > 1) return `${name} ×${quantity}`;
  return name;
}

const SORT_COLUMNS: Array<{ key: TradeBriefingTimberSortKey; label: string; numeric?: boolean }> = [
  { key: 'structure', label: 'Structure' },
  { key: 'name', label: 'Piece' },
  { key: 'size', label: 'Size', numeric: true },
  { key: 'length', label: 'Length', numeric: true },
  { key: 'quantity', label: 'Qty', numeric: true },
];

const TradeBriefingTimberList: React.FC<TradeBriefingTimberListProps> = ({
  structures,
  projectTitle,
  companyName,
  tradeLabel,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState('');
  const [structureFilter, setStructureFilter] = useState('');
  const [sortKey, setSortKey] = useState<TradeBriefingTimberSortKey>('structure');
  const [sortDir, setSortDir] = useState<TradeBriefingTimberSortDir>('asc');

  const rows = useMemo(() => buildTradeBriefingTimberDisplayRows(structures), [structures]);
  const boardFeet = useMemo(() => sumTradeBriefingBoardFeet(structures), [structures]);
  const showSpecies = rows.some(row => row.species);
  const showGrade = rows.some(row => row.grade);
  const showDryness = rows.some(row => row.dryness);
  const structuresInList = useMemo(() => uniqueTradeBriefingStructures(rows), [rows]);
  const visibleRows = useMemo(
    () =>
      sortTradeBriefingTimberRows(
        filterTradeBriefingTimberRows(rows, { query, structure: structureFilter }),
        sortKey,
        sortDir,
      ),
    [rows, query, structureFilter, sortKey, sortDir],
  );
  const deckingItems = useMemo(
    () =>
      structures.flatMap(structure =>
        (structure.deckingItems ?? [])
          .filter(() =>
            !structureFilter || structure.name.trim().toLowerCase() === structureFilter.trim().toLowerCase(),
          )
          .map(item => ({
            ...item,
            structureName: structure.name,
          })),
      ),
    [structures, structureFilter],
  );

  if (!rows.length && !deckingItems.length) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadTradeBriefingTimberPdf({
        structures,
        projectTitle,
        companyName,
        tradeLabel,
      });
    } catch (err) {
      console.error('Trade briefing timber PDF:', err);
      alert('Could not create the timber PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSort = (key: TradeBriefingTimberSortKey) => {
    const next = nextTradeBriefingTimberSort(sortKey, sortDir, key);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const extraColumns = [
    ...(showSpecies ? [{ key: 'species' as const, label: 'Species' }] : []),
    ...(showGrade ? [{ key: 'grade' as const, label: 'Grade' }] : []),
    ...(showDryness ? [{ key: 'dryness' as const, label: 'Dryness' }] : []),
  ];
  const columns = [...SORT_COLUMNS, ...extraColumns];
  const filtered = Boolean(query.trim() || structureFilter);

  return (
    <section className="tb-section" aria-labelledby="tb-timber-heading">
      <div className="tb-section-head">
        <div>
          <p className="tb-kicker">Timber</p>
          <h2 id="tb-timber-heading">Sizes and lengths</h2>
        </div>
        {rows.length ? (
          <button
            type="button"
            className="tb-btn tb-btn-ghost"
            onClick={() => void handleDownload()}
            disabled={downloading}
          >
            {downloading ? <Loader2 size={14} className="tb-spin" aria-hidden /> : null}
            Download PDF
          </button>
        ) : null}
      </div>

      {rows.length ? (
        <>
          <div className="tb-timber-tools">
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter pieces, sizes…"
              aria-label="Filter timber list"
            />
            {structuresInList.length > 1 ? (
              <select
                value={structureFilter}
                onChange={e => setStructureFilter(e.target.value)}
                aria-label="Filter by structure"
              >
                <option value="">All structures</option>
                {structuresInList.map(name => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <div className="tb-table-wrap">
            <table className="tb-table">
              <thead>
                <tr>
                  {columns.map(column => {
                    const active = sortKey === column.key;
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className={column.key === 'quantity' ? 'tb-table-num' : undefined}
                      >
                        <button
                          type="button"
                          className="tb-sort"
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                          <span aria-hidden>{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length ? (
                  visibleRows.map((row, index) => (
                    <tr key={`${row.structureName}-${row.name}-${row.size}-${index}`}>
                      <td>{structureLabel(row.structureName, row.structureQuantity)}</td>
                      <td>{row.name}</td>
                      <td>{row.size || '—'}</td>
                      <td>{row.length || '—'}</td>
                      <td className="tb-table-num">{row.quantity}</td>
                      {showSpecies ? <td>{row.species || '—'}</td> : null}
                      {showGrade ? <td>{row.grade || '—'}</td> : null}
                      {showDryness ? <td>{row.dryness || '—'}</td> : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length}>No pieces match this filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {boardFeet > 0 ? (
        <p className="tb-table-foot">
          Total {formatTradeBriefingBoardFeet(boardFeet)}
          {filtered ? ` · Showing ${visibleRows.length} of ${rows.length}` : ''}
        </p>
      ) : null}

      {deckingItems.length ? (
        <div className="tb-decking">
          <p className="tb-kicker">Decking</p>
          <div className="tb-table-wrap">
            <table className="tb-table">
              <thead>
                <tr>
                  <th scope="col">Structure</th>
                  <th scope="col">Product</th>
                  <th scope="col">Coverage</th>
                  <th scope="col">Board</th>
                </tr>
              </thead>
              <tbody>
                {deckingItems.map(item => (
                  <tr key={`${item.structureName}-${item.id}`}>
                    <td>{item.structureName}</td>
                    <td>{item.productName || 'Decking'}</td>
                    <td>{item.sqFt} sq ft</td>
                    <td>{item.widthInches ? `${item.widthInches}"` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TradeBriefingTimberList;
